import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';

/**
 * Tiny offline drill library so /drills isn't a dead screen when Supabase
 * is the placeholder env. Spec §18.3 calls for ~200 platform drills as
 * the v1 seed; these 16 are the minimum to make every phase chip return
 * something and to demonstrate the Coach's Corner curated source type.
 *
 * Schema-compatible with the `drills` table, so when a real backend lands
 * the same shape is read by `useDrillLibrary`.
 */
export interface OfflineDrill {
  id: string;
  name: string;
  description: string;
  drill_type: string;
  primary_phase: PhaseOfPlay;
  sub_themes: string[];
  duration_mins: number;
  source_type: 'PLATFORM' | 'CURATED_EXPERT';
  curator_name: string | null;
  curator_credentials: string | null;
}

export const OFFLINE_DRILLS: OfflineDrill[] = [
  // Attacking
  {
    id: 'offline-att-1',
    name: '4v2 rondo with switch',
    description: 'Two-square rondo. Six passes then switch sides through the seam.',
    drill_type: 'TECHNICAL',
    primary_phase: 'attacking',
    sub_themes: ['width', 'switch', 'first touch'],
    duration_mins: 12,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-att-2',
    name: 'Wide overload 3v2',
    description: 'Wing-back + winger + 10 against 2 defenders. Cross by 6 seconds.',
    drill_type: 'TACTICAL',
    primary_phase: 'attacking',
    sub_themes: ['overload', 'crossing', 'tempo'],
    duration_mins: 18,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-att-3',
    name: 'Third-man combination',
    description: 'Set-bounce-spin pattern through the half-spaces.',
    drill_type: 'TACTICAL',
    primary_phase: 'attacking',
    sub_themes: ['combinations', 'half-space', 'movement'],
    duration_mins: 15,
    source_type: 'CURATED_EXPERT',
    curator_name: 'Ben Petty',
    curator_credentials: 'FA Youth Coach Educator',
  },
  {
    id: 'offline-att-4',
    name: 'Box arrival 5v4+GK',
    description: 'Build out, finishing arrival. Reset every 90s.',
    drill_type: 'SMALL_SIDED_GAME',
    primary_phase: 'attacking',
    sub_themes: ['finishing', 'arrival', 'box entries'],
    duration_mins: 20,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },

  // Defending
  {
    id: 'offline-def-1',
    name: 'Pressing trigger 4v4+2',
    description: 'Press on first touch from the centre-back. Recover if broken.',
    drill_type: 'TACTICAL',
    primary_phase: 'defending',
    sub_themes: ['press', 'trigger', 'cover-shadow'],
    duration_mins: 15,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-def-2',
    name: 'Block + recover 5v5',
    description: 'Mid-block, force outside, recover with the ball-near full-back.',
    drill_type: 'TACTICAL',
    primary_phase: 'defending',
    sub_themes: ['block', 'force outside', 'recovery'],
    duration_mins: 18,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-def-3',
    name: '1v1 defending lanes',
    description: 'Channel work — stay on inside foot, force the outside touch.',
    drill_type: 'TECHNICAL',
    primary_phase: 'defending',
    sub_themes: ['1v1', 'body shape', 'angle'],
    duration_mins: 10,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-def-4',
    name: 'Backline shape under pressure',
    description: 'Four defenders + GK vs three forwards rotating. Hold the line.',
    drill_type: 'TACTICAL',
    primary_phase: 'defending',
    sub_themes: ['line', 'shape', 'communication'],
    duration_mins: 16,
    source_type: 'CURATED_EXPERT',
    curator_name: 'Ben Petty',
    curator_credentials: 'FA Youth Coach Educator',
  },

  // Transitions
  {
    id: 'offline-tra-1',
    name: 'Counter-press 5 seconds',
    description: 'Lose the ball, win it back inside 5s or transition to defend.',
    drill_type: 'TACTICAL',
    primary_phase: 'transitions',
    sub_themes: ['counter-press', 'recovery', 'urgency'],
    duration_mins: 14,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-tra-2',
    name: 'Turnover to vertical',
    description: 'Win the ball, two touches and play forward. Score in 6 seconds.',
    drill_type: 'SMALL_SIDED_GAME',
    primary_phase: 'transitions',
    sub_themes: ['vertical', 'first 3 seconds', 'tempo'],
    duration_mins: 16,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-tra-3',
    name: 'Defend → attack switch',
    description: 'GK distribution starts the wave; switch the play within 3 passes.',
    drill_type: 'TACTICAL',
    primary_phase: 'transitions',
    sub_themes: ['gk distribution', 'switch', 'wave'],
    duration_mins: 18,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-tra-4',
    name: 'Set-piece transition',
    description: 'Defend a corner then break in numbers within 8 seconds.',
    drill_type: 'TACTICAL',
    primary_phase: 'transitions',
    sub_themes: ['set-piece', 'break', 'numbers up'],
    duration_mins: 12,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },

  // Individual
  {
    id: 'offline-ind-1',
    name: '1v1 attacking with shoulder',
    description: 'Receive on back foot, shoulder drop, beat full-back outside.',
    drill_type: 'TECHNICAL',
    primary_phase: 'individual',
    sub_themes: ['1v1', 'feint', 'first touch'],
    duration_mins: 10,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-ind-2',
    name: 'Scanning circuit',
    description: 'Receive after a scan; play the open side every time.',
    drill_type: 'TECHNICAL',
    primary_phase: 'individual',
    sub_themes: ['scanning', 'awareness', 'decision'],
    duration_mins: 12,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-ind-3',
    name: 'Composure under press',
    description: 'Tight square, two pressers, must complete 5 in a row.',
    drill_type: 'TECHNICAL',
    primary_phase: 'individual',
    sub_themes: ['composure', 'first touch', 'decision'],
    duration_mins: 10,
    source_type: 'PLATFORM',
    curator_name: null,
    curator_credentials: null,
  },
  {
    id: 'offline-ind-4',
    name: 'Receiving on the half-turn',
    description: 'Open hips, take the ball with the back foot, attack the line.',
    drill_type: 'TECHNICAL',
    primary_phase: 'individual',
    sub_themes: ['half-turn', 'body shape', 'first touch'],
    duration_mins: 10,
    source_type: 'CURATED_EXPERT',
    curator_name: 'Ben Petty',
    curator_credentials: 'FA Youth Coach Educator',
  },
];

export interface OfflineDrillFilter {
  phase?: PhaseOfPlay | null;
  curatedOnly?: boolean;
  limit?: number;
}

export function loadOfflineDrills(opts: OfflineDrillFilter = {}): OfflineDrill[] {
  const { phase = null, curatedOnly = false, limit = 60 } = opts;
  let out = OFFLINE_DRILLS.slice();
  if (phase) out = out.filter((d) => d.primary_phase === phase);
  if (curatedOnly) out = out.filter((d) => d.source_type === 'CURATED_EXPERT');
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out.slice(0, limit);
}
